<template>
  <div class="tw-mt-5 tw-mb-5 tw-mr-9">
    <base-advanced-select
      label="Medications"
      placeholder="Select one or more Medications"
      :options="groupedAndSortedMedications"
      :value="selectedMedications"
      @input="onMedicationsChange"
      label-key="label"
      value-key="value"
      multiple
      class="tw-flex-grow"
    />
    <base-advanced-select
      label="Rx Items"
      placeholder="Select one or more Rx Items"
      :options="groupedAndSortedRxItems"
      :value="selectedRxItems"
      @input="onRxItemsChange"
      label-key="label"
      value-key="value"
      multiple
      class="tw-flex-grow tw-mt-4"
    />
  </div>
</template>

<script>
import { orderBy } from "lodash";

export default {
  name: "EditBusinessOrderPrescription",
  props: {
    allowedMedications: {
      type: Array,
      default: () => [],
    },
    allowedRxItems: {
      type: Array,
      default: () => [],
    },
    selectedMedicationIds: {
      type: Array,
      default: () => [],
    },
    selectedRxItemIds: {
      type: Array,
      default: () => [],
    },
    isSaving: Boolean,
  },
  data() {
    return {
      selectedMedications: [],
      selectedRxItems: [],
    };
  },
  computed: {
    groupedAndSortedMedications() {
      const options = [];

      const groups = [...new Set(this.allowedMedications.map((item) => item.productGroup))].filter(Boolean);

      groups.sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        if (!isNaN(numA)) return -1;
        if (!isNaN(numB)) return 1;
        return a.localeCompare(b);
      });

      groups.forEach((group) => {
        options.push({ label: group, value: group, isHeader: true });

        const medsInGroup = this.allowedMedications
          .filter((p) => p.productGroup === group)
          .map((p) => ({
            label: p && p.name && p.name.value ? p.name.value : p.label || "Unnamed Medication",
            value: p ? p.id || p.value : null,
          }));

        options.push(...orderBy(medsInGroup, ["label"]));
      });

      const noGroupItems = this.allowedMedications.filter((item) => !item.productGroup);

      if (noGroupItems.length > 0) {
        options.push({ label: "Other Medications", value: "Other Medications", isHeader: true });

        const mappedNoGroupItems = noGroupItems.map((p) => ({
          label: p && p.name && p.name.value ? p.name.value : p.label || "Unnamed Medication",
          value: p ? p.id || p.value : null,
        }));

        options.push(...orderBy(mappedNoGroupItems, ["label"]));
      }

      return options;
    },

    groupedAndSortedRxItems() {
      const options = [];
      const groups = [...new Set(this.allowedRxItems.map((item) => item.productGroup))].filter(Boolean);

      groups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = this.allowedRxItems
          .filter((p) => p.productGroup === group)
          .map((p) => ({
            label: (p.name && p.name.value) || p.label || "Unnamed Rx Item",
            value: p.id || p.value || null,
          }));
        options.push(...orderBy(itemsInGroup, ["label"]));
      });

      const noGroupItems = this.allowedRxItems.filter((item) => !item.productGroup);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Rx Items", value: null, isHeader: true });
        const mappedNoGroupItems = noGroupItems.map((p) => ({
          label: (p.name && p.name.value) || p.label || "Unnamed Rx Item",
          value: p.id || p.value || null,
        }));
        options.push(...orderBy(mappedNoGroupItems, ["label"]));
      }

      return options;
    },
  },
  watch: {
    selectedMedicationIds: {
      immediate: true,
      handler(newVal) {
        this.selectedMedications = this.allowedMedications
          .filter((med) => newVal.includes(med.id))
          .map((med) => ({ label: med.name.value || med.label || "Unnamed Medication", value: med.id }));
      },
    },
    selectedRxItemIds: {
      immediate: true,
      handler(newVal) {
        this.selectedRxItems = this.allowedRxItems.filter((rx) => newVal.includes(rx.value)).map((rx) => ({ label: rx.label, value: rx.value }));
      },
    },
  },

  methods: {
    onMedicationsChange(newSelected) {
      const ids = newSelected.map((item) => item.value);
      this.selectedMedications = newSelected;
      this.$emit("update:selectedMedicationIds", ids);
    },
    onRxItemsChange(newSelected) {
      const ids = newSelected.map((item) => item.value);
      this.selectedRxItems = newSelected;
      this.$emit("update:selectedRxItemIds", ids);
    },
  },
  created() {
    // Initialize selectedMedications and selectedRxItems from IDs
    this.selectedMedications = this.allowedMedications
      .filter((med) => this.selectedMedicationIds.includes(med.id))
      .map((med) => ({ label: med.name.value || med.label || "Unnamed Medication", value: med.id }));
    this.selectedRxItems = this.allowedRxItems.filter((rx) => this.selectedRxItemIds.includes(rx.value)).map((rx) => ({ label: rx.label, value: rx.value }));
  },
};
</script>
